import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, Minus, Plus, Search, CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const NOLOC = 'No location';

function CountCard({ line, onSave, saving }) {
  const [val, setVal] = useState(line.counted ?? '');
  const [packs, setPacks] = useState('');
  const [loose, setLoose] = useState('');
  useEffect(() => { setVal(line.counted ?? ''); }, [line.counted]);
  const up = Number(line.units_per_pack) || 0;
  const fromPacks = (p, l) => { setPacks(p); setLoose(l); if (p !== '' || l !== '') { setVal(String((Number(p) || 0) * up + (Number(l) || 0))); } };
  const bump = (d) => setVal(String(Math.max(0, (Number(val) || 0) + d)));
  const done = line.counted !== null && line.counted !== undefined;
  return (
    <div className={`rounded-2xl border p-4 bg-white transition-colors ${done ? 'border-green-300 bg-green-50/40' : 'border-slate-200'}`} data-testid={`mc-card-${line.item_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 leading-tight">{line.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{[line.sku, line.barcode, line.unit].filter(Boolean).join(' · ')}</p>
        </div>
        {done && <span className="shrink-0 text-xs font-semibold text-green-700 flex items-center gap-1" data-testid={`mc-done-${line.item_id}`}><Check className="h-3.5 w-3.5" /> {line.counted}</span>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={() => bump(-1)} className="h-12 w-12 rounded-xl border bg-slate-50 active:bg-slate-200 flex items-center justify-center" aria-label="minus one"><Minus className="h-5 w-5" /></button>
        <input inputMode="decimal" type="number" min="0" step="any" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Qty"
          className="h-12 flex-1 min-w-0 rounded-xl border text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`mc-qty-${line.item_id}`} />
        <button type="button" onClick={() => bump(1)} className="h-12 w-12 rounded-xl border bg-slate-50 active:bg-slate-200 flex items-center justify-center" aria-label="plus one"><Plus className="h-5 w-5" /></button>
      </div>
      {up > 0 && (
        <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
          <input inputMode="numeric" type="number" min="0" value={packs} onChange={(e) => fromPacks(e.target.value, loose)} placeholder={line.pack_name || 'packs'} className="h-10 w-20 rounded-lg border text-center" data-testid={`mc-packs-${line.item_id}`} />
          <span>× {up} +</span>
          <input inputMode="numeric" type="number" min="0" value={loose} onChange={(e) => fromPacks(packs, e.target.value)} placeholder="loose" className="h-10 w-20 rounded-lg border text-center" data-testid={`mc-loose-${line.item_id}`} />
        </div>
      )}
      <button type="button" disabled={val === '' || saving} onClick={() => onSave(line, Number(val))}
        className="mt-3 w-full h-12 rounded-xl bg-slate-900 text-white font-semibold disabled:opacity-40 active:scale-[0.99] transition-transform" data-testid={`mc-save-${line.item_id}`}>
        {done ? 'Update count' : 'Save count'}
      </button>
    </div>
  );
}

export default function StocktakeCount() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [loc, setLoc] = useState(null);
  const [q, setQ] = useState('');
  const [todoOnly, setTodoOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(false);
  const searchRef = useRef(null);

  const load = () => api.get(`/general-inventory/stocktakes/${id}`).then((r) => setSt(r.data)).catch(() => toast.error('Could not load stocktake'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const g = {};
    (st?.lines || []).forEach((l) => { const k = l.location || NOLOC; (g[k] = g[k] || []).push(l); });
    return g;
  }, [st]);

  if (!st) { return <div className="p-6 text-slate-500">Loading…</div>; }
  const done = st.status !== 'In Progress';
  const counted = st.lines.filter((l) => l.counted != null).length;
  const locs = Object.keys(groups).sort();

  const save = async (line, value) => {
    setSaving(true);
    try {
      await api.patch(`/general-inventory/stocktakes/${id}/count`, { item_id: line.item_id, counted: value });
      setSt((s) => ({ ...s, lines: s.lines.map((l) => (l.item_id === line.item_id ? { ...l, counted: value } : l)) }));
      setOffline(false);
      toast.success(`${line.name}: ${value}`);
      setQ('');
    } catch (e) {
      if (!e.response) { setOffline(true); }
      toast.error(e.response?.data?.detail || 'Not saved — check your connection and tap Save again');
    } finally { setSaving(false); }
  };

  const term = q.trim().toLowerCase();
  const pool = term ? st.lines : (groups[loc] || []);
  const shown = pool.filter((l) => (!term || `${l.name} ${l.sku} ${l.barcode}`.toLowerCase().includes(term)) && (!todoOnly || l.counted == null));
  const nextLoc = loc ? locs[locs.indexOf(loc) + 1] : null;

  return (
    <div className="min-h-screen bg-slate-100 pb-24 max-w-xl mx-auto" data-testid="mobile-count-page">
      <header className="sticky top-0 z-20 bg-slate-900 text-white px-4 pt-3 pb-3 shadow">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => (loc && !term ? setLoc(null) : navigate('/general-inventory'))} className="p-1 -ml-1" aria-label="back" data-testid="mc-back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-300">{st.number}</p>
            <p className="font-semibold truncate">{loc && !term ? loc : st.name}</p>
          </div>
          {offline && <CloudOff className="h-5 w-5 text-amber-300" />}
          <span className="text-sm font-semibold" data-testid="mc-progress">{counted}/{st.lines.length}</span>
        </div>
        <div className="h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden"><div className="h-full bg-green-400 transition-all" style={{ width: `${(counted / Math.max(st.lines.length, 1)) * 100}%` }} /></div>
        {!done && (
          <div className="relative mt-3">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or scan barcode" className="w-full h-10 rounded-lg pl-9 pr-3 text-slate-900" data-testid="mc-search" />
          </div>
        )}
      </header>

      {done && <p className="m-4 p-3 rounded-lg bg-green-100 text-green-800 text-sm">This stocktake is finalised — counts can no longer be changed.</p>}

      {!done && !loc && !term && (
        <div className="p-4 space-y-2" data-testid="mc-locations">
          <p className="text-sm text-slate-600 mb-1">Pick where you are counting. Others can count other areas at the same time.</p>
          {locs.map((k) => {
            const c = groups[k].filter((l) => l.counted != null).length;
            const all = c === groups[k].length;
            return (
              <button key={k} type="button" onClick={() => setLoc(k)} className="w-full flex items-center justify-between bg-white rounded-2xl border px-4 py-4 active:bg-slate-50" data-testid={`mc-loc-${k}`}>
                <span className="text-left"><span className="block font-semibold">{k}</span><span className="text-xs text-slate-500">{c} of {groups[k].length} counted</span></span>
                {all ? <Check className="h-5 w-5 text-green-600" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
              </button>
            );
          })}
        </div>
      )}

      {!done && (loc || term) && (
        <div className="p-4 space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={todoOnly} onChange={(e) => setTodoOnly(e.target.checked)} data-testid="mc-todo-only" /> Show only items not counted yet</label>
          {shown.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{term ? 'No item matches — check the code or add it on the paper sheet.' : 'Everything here is counted.'}</p>}
          {shown.map((l) => <CountCard key={l.item_id} line={l} onSave={save} saving={saving} />)}
          {!term && nextLoc && (
            <button type="button" onClick={() => { setLoc(nextLoc); window.scrollTo(0, 0); }} className="w-full h-12 rounded-xl border-2 border-slate-900 font-semibold flex items-center justify-center gap-1" data-testid="mc-next-loc">
              Next: {nextLoc} <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {!term && !nextLoc && <button type="button" onClick={() => setLoc(null)} className="w-full h-12 rounded-xl border font-semibold" data-testid="mc-all-locs">Back to all locations</button>}
        </div>
      )}
    </div>
  );
}
